/**
 * PageTitleContext — single source of truth for the on-screen page
 * title so the Header breadcrumb can never drift from what a page's
 * <PageHero> / <PageHeroCompact> actually renders.
 *
 * Flow:
 *   - Each hero calls `useRegisterPageTitle(title)`, which publishes
 *     { title, path } whenever the title or route changes.
 *   - The Header calls `usePageTitle()` and uses the live title only
 *     when its registered `path` matches the current route (prevents
 *     showing a stale title from the previous page during a
 *     transition). Pages without a hero fall back to the Header's
 *     static `pageTitles` map.
 *
 * Used outside the provider (e.g. public /share pages that still use
 * PageHero) the register hook is a harmless no-op via the default
 * context value.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

const PageTitleContext = createContext({
  title: null,
  path: null,
  registerPageTitle: () => {},
});

export function PageTitleProvider({ children }) {
  const [state, setState] = useState({ title: null, path: null });

  const registerPageTitle = useCallback((title, path) => {
    setState((prev) => {
      if (prev.title === title && prev.path === path) return prev;
      return { title, path };
    });
  }, []);

  return (
    <PageTitleContext.Provider value={{ ...state, registerPageTitle }}>
      {children}
    </PageTitleContext.Provider>
  );
}

/**
 * Publish the live on-screen title. Only string titles are
 * registered — JSX/dynamic titles are skipped so the Header falls
 * back to its static map for those routes.
 */
export function useRegisterPageTitle(title) {
  const { registerPageTitle } = useContext(PageTitleContext);
  const location = useLocation();
  useEffect(() => {
    if (typeof title !== 'string' || !title.trim()) return;
    registerPageTitle(title.trim(), location.pathname);
  }, [title, location.pathname, registerPageTitle]);
}

/** Read the currently-registered { title, path }. */
export function usePageTitle() {
  return useContext(PageTitleContext);
}

export default PageTitleContext;
